/**
 * **«Ver como rol»**: la vista previa que el Dueño usa para recorrer el sistema con los
 * ojos de otro rol (pedido de Viva, 13-oct-2026).
 *
 * Es **sólo de front** y a propósito:
 * - el rol simulado filtra lo que se dibuja (menú, rutas, botones que cada pantalla
 *   muestra según el rol); NO cambia la identidad frente a la API — los requests siguen
 *   saliendo con la sesión real del Dueño;
 * - por eso mismo, mientras la vista previa está activa **ningún request de escritura
 *   sale del navegador** (`assertPreviewAllows`, lo llama `lib/api.ts`): no se guarda, no
 *   se cobra, no se borra nada. Si la sesión es del Dueño y el request sale, quedaría
 *   imputado a él con permisos de Dueño aunque la pantalla fuera la de Recepción.
 *
 * Es un módulo con estado propio (como `session.ts`) para que `lib/api.ts` lo lea sin
 * depender de React. Vive en `sessionStorage`: sobrevive a un recargar de la pestaña, pero
 * no se contagia a otra pestaña ni a otro día.
 */
import type { UserRole } from "@viva/shared";
import type { RequestOptions } from "../api.js";
import { getSurface } from "./session.js";

const KEY_PREVIEW = "viva.rolePreview.v1";

export interface RolePreview {
  /** El rol que se está simulando. */
  role: UserRole;
  /** La sede con la que se mira (la sede activa durante la vista previa). */
  branchId: string;
  /** La sede activa real antes de entrar: se restaura al salir. */
  previousBranchId: string | null;
}

/** Mensaje único del bloqueo: lo ve la pantalla que intentó guardar y el aviso global. */
export const PREVIEW_BLOCKED_MESSAGE =
  "Estás en la vista previa de un rol: no se puede guardar, cobrar ni borrar nada. Salí de la vista previa para operar.";

/**
 * Requests `POST` que **no escriben**: calculan algo y devuelven el resultado. Se dejan
 * pasar para que las pantallas se vean como de verdad (la cotización de un cobro, la
 * vista previa de un flyer). Cerrar sesión también: salir nunca se bloquea.
 */
const READ_ONLY_POSTS: readonly string[] = [
  "/api/payments/quote",
  "/api/flyers/render-data",
  "/api/auth/logout",
];

type Listener = () => void;

let preview: RolePreview | null = null;
let hydrated = false;
const blockedListeners = new Set<Listener>();

function safeStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  try {
    const raw = safeStorage()?.getItem(KEY_PREVIEW);
    preview = raw ? (JSON.parse(raw) as RolePreview) : null;
  } catch {
    preview = null;
  }
}

export function getRolePreview(): RolePreview | null {
  hydrate();
  return preview;
}

export function setRolePreview(next: RolePreview): void {
  hydrated = true;
  preview = next;
  try {
    safeStorage()?.setItem(KEY_PREVIEW, JSON.stringify(next));
  } catch {
    /* sin storage la vista previa dura lo que dure la pestaña abierta */
  }
}

export function clearRolePreview(): void {
  hydrated = true;
  preview = null;
  try {
    safeStorage()?.removeItem(KEY_PREVIEW);
  } catch {
    /* nada que limpiar */
  }
}

/** Se entera de cada escritura frenada (el banner muestra el aviso). */
export function onPreviewBlocked(listener: Listener): () => void {
  blockedListeners.add(listener);
  return () => blockedListeners.delete(listener);
}

/** `true` si este request escribe algo (todo lo que no es GET, salvo los POST de lectura). */
export function isWriteRequest(method: RequestOptions["method"], path: string): boolean {
  const verb = method ?? "GET";
  if (verb === "GET") return false;
  if (verb !== "POST") return true;
  const pathname = path.startsWith("http") ? new URL(path).pathname : path.split("?")[0]!;
  return !READ_ONLY_POSTS.includes(pathname);
}

/**
 * ¿Hay que frenar este request? `true` durante la vista previa si escribe algo; avisa a
 * los que escuchan y `lib/api.ts` lo corta antes de que salga. La tablet kiosko opera con
 * su propia identidad de dispositivo, no con la del Dueño: ahí no aplica.
 */
export function previewBlocks(method: RequestOptions["method"], path: string): boolean {
  if (!getRolePreview() || getSurface() === "kiosk") return false;
  if (!isWriteRequest(method, path)) return false;
  for (const listener of blockedListeners) listener();
  return true;
}

/** Sólo tests. */
export function resetPreviewForTests(): void {
  clearRolePreview();
  hydrated = false;
  blockedListeners.clear();
}
